import { useQuery } from "@tanstack/react-query";

import {
  CAD_USD_FEED,
  CHAINLINK_AGGREGATOR_ABI,
  USDC_USD_FEED,
} from "@/config/chainlink-feeds";
import { CADC, USDC } from "@/config/tokens";
import { buildPublicClient } from "@/lib/wallet/clients";

export type ChainlinkFx = {
  /** Raw CAD/USD answer at 8 decimals (e.g. 72_545_250 = $0.7254 per CAD). */
  readonly cadPrice: bigint;
  /** Raw USDC/USD answer at 8 decimals. */
  readonly usdcPrice: bigint;
  readonly cadDecimals: number;
  readonly usdcDecimals: number;
};

/**
 * Inverse of the contract's `_swapCadcForUsdc` Chainlink sizing: how much CADC
 * (raw 1e18) is needed to obtain `usdcOut` (raw 1e6) at the FX rate the looper
 * sees on-chain. Pure `bigint` throughout so the result can be used directly
 * as a contract-bound amount.
 */
export const cadcNeededForUsdc = (usdcOut: bigint, fx: ChainlinkFx): bigint => {
  if (usdcOut === 0n) return 0n;
  const numerator =
    usdcOut *
    fx.usdcPrice *
    10n ** BigInt(fx.cadDecimals) *
    10n ** BigInt(CADC.decimals);
  const denominator =
    fx.cadPrice *
    10n ** BigInt(fx.usdcDecimals) *
    10n ** BigInt(USDC.decimals);
  if (denominator === 0n) return 0n;
  return numerator / denominator;
};

/**
 * Reads the same CAD/USD and USDC/USD Chainlink feeds the looper uses for
 * its on-chain swap math, so the frontend can mirror the contract's
 * Chainlink-anchored quote when sizing `collateralToSwap` for
 * `unwindBySwap`. Both reads share the multicall batch with the rest of the
 * page's queries.
 */
export const useChainlinkFx = () =>
  useQuery({
    queryKey: ["chainlink-fx"],
    queryFn: async (): Promise<ChainlinkFx> => {
      const client = buildPublicClient();
      const [cadPrice, cadDecimals, usdcPrice, usdcDecimals] =
        await client.multicall({
          allowFailure: false,
          contracts: [
            {
              address: CAD_USD_FEED,
              abi: CHAINLINK_AGGREGATOR_ABI,
              functionName: "latestAnswer",
            },
            {
              address: CAD_USD_FEED,
              abi: CHAINLINK_AGGREGATOR_ABI,
              functionName: "decimals",
            },
            {
              address: USDC_USD_FEED,
              abi: CHAINLINK_AGGREGATOR_ABI,
              functionName: "latestAnswer",
            },
            {
              address: USDC_USD_FEED,
              abi: CHAINLINK_AGGREGATOR_ABI,
              functionName: "decimals",
            },
          ],
        });
      return {
        cadPrice,
        cadDecimals,
        usdcPrice,
        usdcDecimals,
      };
    },
  });
